// src/components/LanguageSwitcher.jsx
//
// Branded language dropdown — replaces the native <select> (which renders the
// OS/grey control and clashes with the Puzzly design language). Custom trigger
// + popover, keyboard + click-outside aware, styled with the global brand
// tokens defined in public/index.html (:root).
//
// Two tones:
//   nav  — compact pill for the marketing header (shows the language CODE, e.g. "ES")
//   menu — full-label control for the in-app Account sheet (shows "Español")
//
// Used by Hero.jsx (nav) and AccountMenu.jsx (menu).
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, LANG_LABELS } from "../i18n";

const TONES = {
  nav: {
    font: "var(--font-display)",
    surface: "var(--paper, #fff)",
    text: "var(--ink, #2a0a18)",
    border: "var(--ink, #2a0a18)",
    triggerBg: "transparent",
    hover: "var(--tonal-1, #f3e8de)",
    accent: "var(--primary, #a8275a)",
    accentSoft: "var(--tonal-1, #f3e8de)",
    shadow: "var(--shadow-2, 0 18px 40px -16px rgba(122,14,60,.28))",
    triggerLabel: (lng) => lng.toUpperCase(),
    fontWeight: 700,
  },
  menu: {
    font: "var(--font-body)",
    surface: "var(--color-surface, var(--paper, #fff))",
    text: "var(--color-text, var(--ink, #2a0a18))",
    border: "var(--color-border, var(--tonal-2, #e8d4c2))",
    triggerBg: "var(--color-surface, var(--paper, #fff))",
    hover: "var(--tonal-1, rgba(168,39,90,.08))",
    accent: "var(--primary, #a8275a)",
    accentSoft: "rgba(168,39,90,.12)",
    shadow: "0 14px 32px -14px rgba(42,10,24,.45)",
    triggerLabel: (lng) => LANG_LABELS[lng] || lng.toUpperCase(),
    fontWeight: 600,
  },
};

export default function LanguageSwitcher({ tone = "nav", showGlobe = true, align = "end" }) {
  const { t, i18n } = useTranslation();
  const cfg = TONES[tone] || TONES.nav;
  const current = i18n.resolvedLanguage || i18n.language || "en";
  const currentLng = SUPPORTED_LANGS.includes(current) ? current : "en";

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(
    Math.max(0, SUPPORTED_LANGS.indexOf(currentLng))
  );
  const rootRef = useRef(null);
  const btnRef = useRef(null);

  const label = t("common.language.label", { defaultValue: "Language" });

  const choose = useCallback(
    (lng) => {
      i18n.changeLanguage(lng);
      setOpen(false);
      if (btnRef.current) btnRef.current.focus();
    },
    [i18n]
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Keyboard nav within the open list.
  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setActiveIdx(Math.max(0, SUPPORTED_LANGS.indexOf(currentLng)));
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      if (btnRef.current) btnRef.current.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % SUPPORTED_LANGS.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + SUPPORTED_LANGS.length) % SUPPORTED_LANGS.length);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      choose(SUPPORTED_LANGS[activeIdx]);
    }
  };

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: tone === "nav" ? "8px 12px" : "7px 12px",
          borderRadius: 999,
          border: `1.5px solid ${cfg.border}`,
          background: cfg.triggerBg,
          color: cfg.text,
          fontFamily: cfg.font,
          fontWeight: cfg.fontWeight,
          fontSize: 14,
          lineHeight: 1,
          cursor: "pointer",
          transition: "background .18s, border-color .18s",
          whiteSpace: "nowrap",
        }}
      >
        {showGlobe && <span aria-hidden style={{ fontSize: 14 }}>🌐</span>}
        <span>{cfg.triggerLabel(currentLng)}</span>
        <span
          aria-hidden
          style={{
            fontSize: 10,
            opacity: 0.7,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        >
          ▾
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={label}
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            [align === "end" ? "right" : "left"]: 0,
            zIndex: 1000,
            margin: 0,
            padding: 6,
            listStyle: "none",
            minWidth: 160,
            background: cfg.surface,
            borderRadius: 14,
            boxShadow: cfg.shadow,
            border: `1px solid ${tone === "menu" ? cfg.border : "transparent"}`,
            animation: "pop-in .16s cubic-bezier(.2,.7,.2,1)",
          }}
        >
          {SUPPORTED_LANGS.map((lng, idx) => {
            const selected = lng === currentLng;
            const active = idx === activeIdx;
            return (
              <li
                key={lng}
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => choose(lng)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontFamily: cfg.font,
                  fontSize: 14,
                  fontWeight: selected ? 700 : 500,
                  color: cfg.text,
                  background: selected ? cfg.accentSoft : active ? cfg.hover : "transparent",
                  transition: "background .12s",
                }}
              >
                <span>{LANG_LABELS[lng] || lng.toUpperCase()}</span>
                <span
                  aria-hidden
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: selected ? cfg.accent : "var(--muted, #8a6675)",
                    opacity: selected ? 1 : 0.6,
                  }}
                >
                  {selected ? "✓" : lng.toUpperCase()}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
